import { useState, useRef } from 'react'
import { NextSeo } from 'next-seo'
import jwtDecode from 'jwt-decode'
import moment from 'moment'
import { useRouter } from 'next/router'

import { Typography, Button, Checkbox } from '@evocolabs/orkaui'

import { useSelector } from 'react-redux'
import Header from '@/src/components/HeaderV2'
import RequireLogin from '@/src/utils/RequireLogin'

import { selector } from '@/src/redux/store'
import { EMAIL } from '@/src/redux/state'
import { useBackend } from '@/src/providers/BackendProvider'

import styles from '@/src/components/scss/LegalData.module.scss'

const View1 = ({ setView }) => {
  const { requestDataDeleteAll } = useBackend()
  const loggedUser = useSelector(selector.selectUserInfo).uuid
  const submit = async () => {
    await requestDataDeleteAll(loggedUser)
    setView((d) => d + 1)
  }
  return (
    <div className={styles.wrapper}>
      <Typography variant="h4">Personal Data Deletion Request</Typography>
      <Typography variant="body3">
        We will send you a confirmation to your registered email address{' '}
        <Typography variant="Body3">
          {useSelector(selector.selectUserInfo)[EMAIL]}
        </Typography>{' '}
        to verify your request.
        <br />
        NOTE: Account will NOT BE RECOVERABLE once deleted. If you want to
        delete all personal data, your account will also be deleted.
      </Typography>
      <div className={styles.buttonWrapper}>
        <Button
          variant="primary"
          size="large"
          handleClick={submit}
          loadable
          id="data-deletion-page-submit-button"
        >
          Submit
        </Button>
      </div>
    </div>
  )
}

const View2 = () => {
  return (
    <div className={styles.wrapper}>
      <Typography variant="h4">Submitted</Typography>
      <Typography variant="body3">
        We have send a data deletion request link to your registered email (
        {useSelector(selector.selectUserInfo)[EMAIL]}), please check.
      </Typography>
    </div>
  )
}

const View3 = ({ setView }) => {
  const [error, setError] = useState(null)
  return (
    <div className={styles.wrapper}>
      <Typography variant="h4">Personal Data Deletion Request</Typography>
      <Typography variant="body3">
        We prioritize data privacy and security, and are dedicated to processing
        your request to delete your personal information from our records in
        compliance with relevant laws and regulations.
        <br />
        <br /> Be advised that there may be situations where we cannot delete
        specific personal information, such as when an order is in progress or a
        remote consultation is scheduled. In these instances, we will inform you
        of the rationale for the exception.
        <br />
        <br /> Please be aware that deleting your personal information might
        impact our capacity to deliver services to you, either partially or
        entirely. This action could also result in the suspension or termination
        of an existing order and closure of your account. Upon account closure,
        all information will be deleted, encompassing personal information and
        any data on the Orka App. We may not be able to recover such information
        for you. <br />
        <br />
        Should you have any inquiries or concerns about the deletion request
        procedure, please do not hesitate to contact us at{' '}
        <a href="mailto:privacy@hiorka.com">
          <Typography variant="link">privacy@hiorka.com</Typography>
        </a>
        . <br />
        <br />
        We maintain the right to verify your identity prior to fulfilling your
        request to delete your personal information. <br />
        <br />
        Kindly note that we may retain specific personal information as
        necessary for legal compliance or other legitimate purposes.
      </Typography>
      <Checkbox
        label="I acknowledge and agree the consequences of request to delete my personal information."
        id="confirm_checkbox"
        errormsg={error}
      />
      <Button
        variant="primary"
        size="large"
        handleClick={() => {
          const checkbox = document.getElementById('confirm_checkbox')
          if (checkbox.checked) {
            setView((d) => d + 1)
          } else {
            setError('Please confirm your request.')
          }
        }}
        id="data-deletion-page-acknowledgement-deletion-button"
      >
        Delete Data
      </Button>
    </div>
  )
}

const View4 = ({ setView, token }) => {
  const [error, setError] = useState(null)
  const { confirmDataDeleteAll } = useBackend()
  const loggedUser = useSelector(selector.selectUserInfo).uuid
  return (
    <div className={styles.wrapper}>
      <Typography variant="h4">Personal Data Deletion Request</Typography>
      <Typography variant="body3">
        Your account will be CLOSED if you proceed with this personal
        information deletion request. If your account is closed, all your
        personal information (including any personal information stored on the
        Orka App) will be deleted within 10 business days, during which time you
        may have limited access to your account. Please note that Orka may not
        be able to recover your personal information.
      </Typography>
      <Checkbox
        label="I agree to the above, please proceed."
        id="confirm_checkbox"
        errormsg={error}
      />
      <Button
        variant="primary"
        size="large"
        handleClick={async () => {
          const checkbox = document.getElementById('confirm_checkbox')
          if (checkbox.checked) {
            const res = await confirmDataDeleteAll(loggedUser, token)
            if (res.data.message === 'success') {
              setView((d) => d + 1)
            }
          } else {
            setError('Please confirm your request.')
          }
        }}
        loadable
        id="data-deletion-page-agreement-deletion-button"
      >
        Delete Data
      </Button>
    </div>
  )
}

const View5 = () => {
  const submit = async () => {
    window.location.href = '/'
  }
  return (
    <div className={styles.wrapper}>
      <Typography variant="h4">Successfully Submitted</Typography>
      <Typography variant="body3">
        Your data will be deleted in within 10 business days, and we will notify
        you of the results as soon as deletion is complete. If you have any
        questions, please contact us.
      </Typography>
      <div className={styles.buttonWrapper}>
        <Button
          variant="primary"
          size="large"
          handleClick={submit}
          id="data-deletion-page-submit-button"
        >
          Understand
        </Button>
      </div>
    </div>
  )
}

const Expired = () => {
  const submit = async () => {
    window.location.href = '/legal/DataDeletion'
  }
  return (
    <div className={styles.wrapper}>
      <Typography variant="h4">Error</Typography>
      <Typography variant="body3">
        Your request to delete your personal data has expired. Please try again.
      </Typography>
      <div className={styles.buttonWrapper}>
        <Button
          variant="primary"
          size="large"
          handleClick={submit}
          id="data-deletion-page-request-again-button"
        >
          Request Again
        </Button>
      </div>
    </div>
  )
}

const DataDeletion = () => {
  const router = useRouter()
  const token = router.query.token
  const [view, setView] = useState(-1)
  const loggedUser = useSelector(selector.selectUserInfo).uuid
  const validToken = useRef(true)
  if (token) {
    const decoded = jwtDecode(token)
    // Check if token is expired
    const tokenDate = new Date(decoded.exp * 1000)
    const dateDiff = moment().diff(tokenDate, 'seconds')
    if (dateDiff > 0 && view !== 'expired') {
      setView('expired')
      validToken.current = false
    }
    // Check if token uuid matches user uuid
    if (loggedUser !== decoded.user_id) {
      validToken.current = false
      alert(
        'You are not authorized to access this page. Please sign in with the account you used to request the data.'
      )
      router.push(
        '/SignIn' +
          `?redirect_uri=${encodeURIComponent(
            location.origin + location.pathname + location.search
          )}`
      )
    }
    if (view < 3 && validToken.current) {
      setView(3)
    }
  } else {
    if (view === -1) {
      setView(1)
    }
  }
  return (
    <RequireLogin>
      <style>{`html, body, #vh-relative { background-color: #F7F3EB !important;}`}</style>
      <NextSeo
        title="Delete Personal Data | Orka - Hear what matters"
        description="Delete Personal Data"
      />
      <Header iswhitebg />
      {view === 1 && <View1 setView={setView} />}
      {view === 2 && <View2 />}
      {view === 3 && <View3 setView={setView} />}
      {view === 4 && <View4 setView={setView} token={token} />}
      {view === 5 && <View5 />}
      {view === 'expired' && <Expired />}
    </RequireLogin>
  )
}

export default DataDeletion
